import React from 'react'
import { useSelector } from 'react-redux'

import { Redirect } from 'components/shared/Routes/Redirect/Redirect'

import type { FeatureKey, RoleKey } from 'generated/features'
import { getFeatures, getRoles } from 'ducks/auth'

type RedirectAction = React.ComponentProps<typeof Redirect>['action']

type GateProps = {
  children?: React.ReactNode
  // router action to dispatch when the check fails, after the account has loaded
  redirectTo?: RedirectAction
}

export const useFeature = (key: FeatureKey): boolean =>
  useSelector(getFeatures)[key] === true

export const useRole = (role: RoleKey): boolean =>
  useSelector(getRoles)[role] === true

const Gate = ({
  allowed,
  loaded,
  children,
  redirectTo,
}: GateProps & { allowed: boolean; loaded: boolean }) => {
  if (allowed) return <>{children}</>
  if (redirectTo && loaded) return <Redirect action={redirectTo} />
  return null
}

export const Feature = ({
  name,
  ...props
}: GateProps & { name: FeatureKey }) => (
  <Gate
    allowed={useFeature(name)}
    loaded={Object.keys(useSelector(getFeatures)).length !== 0}
    {...props}
  />
)

export const RequireRole = ({
  role,
  ...props
}: GateProps & { role: RoleKey }) => (
  <Gate
    allowed={useRole(role)}
    loaded={Object.keys(useSelector(getRoles)).length !== 0}
    {...props}
  />
)
